<script setup lang="ts">
import { PhFilePlus, PhFloppyDisk, PhWarning, PhX } from '@phosphor-icons/vue';
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { VueDraggable } from 'vue-draggable-plus';
import { useI18n } from 'vue-i18n';
import VuePdfEmbed from 'vue-pdf-embed';
import PdfMergerFileRow from './PdfMergerFileRow.vue';
import {
  appendPdfFiles,
  isExternalFileDrag,
  usePdfMergePreview,
  type MergeInput,
  type PdfBatchRejectReason,
} from './usePdfMerge';

export type StagedItem = MergeInput;

const props = defineProps<{
  initialItems: StagedItem[];
  maxBytes: number;
}>();

const emit = defineEmits<{
  save: [
    payload: { blob: Blob | null; pageCount: number; items: StagedItem[] },
  ];
  close: [];
}>();

const { t } = useI18n();

const MAX_FILES = 20;

// Working copy — the parent's list is only updated on Save.
const items = ref<StagedItem[]>(
  props.initialItems.map((item) => ({ ...item })),
);
const inlineMessage = ref<string | null>(null);
const fileInput = ref<HTMLInputElement | null>(null);
const isDragging = ref(false);

const { result, isMerging, schedule, reset } = usePdfMergePreview();

const toMergeInputs = (): MergeInput[] =>
  items.value.map(({ id, file }) => ({ id, file }));

// Re-merge on committed changes only (add / remove / completed drop).
const remerge = () => schedule(toMergeInputs());

const pageCountFor = (id: string) =>
  result.value?.ok.find((entry) => entry.id === id)?.pageCount;

const errorFor = (id: string) =>
  result.value?.errored.find((entry) => entry.id === id)?.reason;

const totalBytes = computed(() =>
  items.value.reduce((sum, item) => sum + item.file.size, 0),
);

const totalMb = computed(() => (totalBytes.value / (1024 * 1024)).toFixed(1));
const maxMb = computed(() => Math.round(props.maxBytes / (1024 * 1024)));

const messageForReason = (reason: PdfBatchRejectReason) => {
  switch (reason) {
    case 'notPdf':
      return t('pdfMerger.notPdf');
    case 'tooManyFiles':
      return t('pdfMerger.tooManyFiles', { count: MAX_FILES });
    case 'tooLarge':
      return t('pdfMerger.tooLarge', { maxMb: maxMb.value });
  }
};

const addFiles = (fileList: FileList | File[]) => {
  const result = appendPdfFiles(items.value, fileList, {
    maxBytes: props.maxBytes,
    maxFiles: MAX_FILES,
  });
  items.value = result.items;

  inlineMessage.value = result.rejectionReasons.length
    ? messageForReason(result.rejectionReasons.at(-1)!)
    : null;

  if (result.addedCount > 0) remerge();
};

const onFilePick = (event: Event) => {
  const input = event.target as HTMLInputElement;
  if (input.files) addFiles(input.files);
  input.value = ''; // allow re-picking the same file
};

const DRAG_IDLE_MS = 150;
let dragTimer: ReturnType<typeof setTimeout> | undefined;

const onDragOver = (event: DragEvent) => {
  // Ignore the sortable list's own native reorder drag — only highlight for
  // a real file being dragged in from outside the browser.
  if (!isExternalFileDrag(event)) return;
  isDragging.value = true;
  clearTimeout(dragTimer);
  dragTimer = setTimeout(() => {
    isDragging.value = false;
  }, DRAG_IDLE_MS);
};

const onDrop = (event: DragEvent) => {
  event.preventDefault();
  clearTimeout(dragTimer);
  isDragging.value = false;
  if (event.dataTransfer?.files?.length) addFiles(event.dataTransfer.files);
};

const removeItem = (id: string) => {
  items.value = items.value.filter((item) => item.id !== id);
  inlineMessage.value = null;
  remerge();
};

const previewUrl = ref<string | null>(null);
watch(
  () => result.value?.blob,
  (blob) => {
    if (previewUrl.value) URL.revokeObjectURL(previewUrl.value);
    previewUrl.value = blob ? URL.createObjectURL(blob) : null;
  },
);

const canSave = computed(
  () => !isMerging.value && (!!result.value?.blob || items.value.length === 0),
);

const onSave = () => {
  if (isMerging.value) return;

  if (items.value.length === 0) {
    emit('save', { blob: null, pageCount: 0, items: [] });
    return;
  }

  const current = result.value;
  if (!current?.blob) return;
  emit('save', {
    blob: current.blob,
    pageCount: current.pageCount,
    items: items.value.map((item) => ({ ...item })),
  });
};

onBeforeUnmount(() => {
  reset();
  if (previewUrl.value) URL.revokeObjectURL(previewUrl.value);
});

remerge();
</script>

<template>
  <div class="modal is-active" role="dialog" aria-modal="true">
    <div class="modal-background" @click="emit('close')"></div>

    <div class="modal-card pdf-merger-modal-card">
      <header class="modal-card-head">
        <p class="modal-card-title">{{ t('pdfMerger.modalTitle') }}</p>
        <button
          class="delete"
          type="button"
          :aria-label="t('cancel')"
          @click="emit('close')"
        ></button>
      </header>

      <section class="modal-card-body">
        <div class="pdf-merger-panes">
          <!-- Left: staged, ordered, reorderable list -->
          <div class="pdf-merger-list-pane">
            <div
              class="pdf-merger-scroll"
              :class="{ 'is-dragging-over': isDragging }"
              @dragover.prevent="onDragOver"
              @drop="onDrop"
            >
              <button
                type="button"
                class="pdf-merger-dropzone"
                @click="fileInput?.click()"
              >
                <PhFilePlus :size="22" class="mr-2" />
                {{ t('pdfMerger.addFiles') }}
              </button>
              <input
                ref="fileInput"
                type="file"
                accept="application/pdf"
                multiple
                class="is-hidden"
                @change="onFilePick"
              />

              <p class="pdf-merger-size-counter has-text-grey is-size-7">
                {{ t('pdfMerger.sizeUsage', { used: totalMb, max: maxMb }) }}
              </p>

              <hr class="pdf-merger-divider" />

              <div
                v-if="inlineMessage"
                class="pdf-merger-inline-message"
                role="alert"
              >
                <PhWarning :size="20" weight="bold" class="mr-2" />
                <span class="pdf-merger-inline-message__text">{{
                  inlineMessage
                }}</span>
                <button
                  type="button"
                  class="pdf-merger-inline-message__close"
                  :aria-label="t('notifications.close')"
                  @click="inlineMessage = null"
                >
                  <PhX :size="16" weight="bold" />
                </button>
              </div>

              <p v-if="items.length === 0" class="has-text-grey is-size-7 mt-3">
                {{ t('pdfMerger.empty') }}
              </p>

              <VueDraggable
                v-else
                v-model="items"
                tag="ul"
                class="pdf-merger-list"
                handle=".pdf-merger-row__handle"
                :animation="150"
                ghost-class="is-dragging"
                @update="remerge"
              >
                <PdfMergerFileRow
                  v-for="item in items"
                  :key="item.id"
                  :id="item.id"
                  :file-name="item.file.name"
                  :page-count="pageCountFor(item.id)"
                  :error-reason="errorFor(item.id)"
                  @remove="removeItem"
                />
              </VueDraggable>
            </div>
          </div>

          <div
            class="pdf-merger-preview-pane"
            :class="{ 'is-dragging-over': isDragging }"
            @dragover.prevent="onDragOver"
            @drop="onDrop"
          >
            <div v-if="isMerging" class="pdf-merger-spinner">
              <span class="pdf-merger-spinner__circle" />
              <span class="is-size-7 mt-2">{{ t('pdfMerger.merging') }}</span>
            </div>
            <VuePdfEmbed
              v-else-if="previewUrl"
              :source="previewUrl"
              class="pdf-merger-preview"
            />
            <p v-else class="has-text-grey is-size-7 pdf-merger-placeholder">
              {{ t('pdfMerger.previewPlaceholder') }}
            </p>
          </div>
        </div>
      </section>

      <footer class="modal-card-foot is-justify-content-flex-end">
        <button type="button" class="button" @click="emit('close')">
          <PhX :size="16" class="mr-1" />
          {{ t('cancel') }}
        </button>
        <button
          type="button"
          class="button is-primary"
          :disabled="!canSave"
          @click="onSave"
        >
          <PhFloppyDisk :size="16" class="mr-1" />
          {{ t('save') }}
        </button>
      </footer>
    </div>
  </div>
</template>

<style scoped>
.pdf-merger-modal-card {
  width: 960px;
  max-width: 95vw;
  height: 90vh;
  max-height: calc(100vh - 40px);
}

.pdf-merger-modal-card .modal-card-body {
  display: flex;
  overflow: hidden;
}

.pdf-merger-modal-card .modal-card-foot {
  gap: 0.75rem;
}

.pdf-merger-modal-card .modal-card-head .delete {
  width: 2.25rem;
  height: 2.25rem;
  max-width: none;
  max-height: none;
}

.pdf-merger-panes {
  display: flex;
  gap: 1rem;
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
}

.pdf-merger-list-pane {
  flex: 0 0 38%;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.pdf-merger-scroll {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-gutter: stable;
  border: 2px solid transparent;
  border-radius: 8px;
  transition:
    border-color 0.15s,
    background-color 0.15s;
}

.pdf-merger-scroll.is-dragging-over {
  border-color: #3273dc;
  background-color: rgba(50, 115, 220, 0.06);
}

.pdf-merger-size-counter {
  margin: 0;
  text-align: center;
}

.pdf-merger-divider {
  width: 100%;
  margin: 0;
  border: none;
  border-top: 1px solid #dbdbdb;
}

.pdf-merger-dropzone {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 1rem;
  position: sticky;
  top: 0;
  z-index: 1;
  border: 2px dashed #b5b5b5;
  border-radius: 8px;
  background: #fafafa;
  cursor: pointer;
  color: #4a4a4a;
}

.pdf-merger-dropzone:hover {
  border-color: #3273dc;
  color: #3273dc;
}

.pdf-merger-inline-message {
  display: flex;
  align-items: center;
  width: 100%;
  margin-top: 0.5rem;
  padding: 0.75rem 1rem;
  font-size: 0.85rem;
  color: #cc0f35;
  background-color: rgba(204, 15, 53, 0.08);
  border: 1px solid #cc0f35;
  border-radius: 999px;
}

.pdf-merger-inline-message :deep(svg) {
  flex-shrink: 0;
}

.pdf-merger-inline-message__text {
  flex: 1 1 auto;
  min-width: 0;
}

.pdf-merger-inline-message__close {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-left: 0.75rem;
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  cursor: pointer;
  opacity: 0.7;
}

.pdf-merger-inline-message__close:hover {
  opacity: 1;
}

.pdf-merger-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.pdf-merger-preview-pane {
  flex: 1;
  min-width: 0;
  border: 1px solid #dbdbdb;
  border-radius: 8px;
  background: #f5f5f5;
  overflow: auto;
  display: flex;
  transition:
    border-color 0.15s,
    background-color 0.15s;
}

.pdf-merger-preview-pane.is-dragging-over {
  border-color: #3273dc;
  background-color: rgba(50, 115, 220, 0.06);
}

.pdf-merger-preview {
  width: 100%;
  max-width: 100%;
  align-self: flex-start;
}

.pdf-merger-preview :deep(canvas),
.pdf-merger-preview :deep(svg),
.pdf-merger-preview :deep(img) {
  max-width: 100%;
  height: auto;
}

.pdf-merger-spinner,
.pdf-merger-placeholder {
  margin: auto;
}

.pdf-merger-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #7a7a7a;
}

.pdf-merger-spinner__circle {
  position: relative;
  width: 3rem;
  height: 3rem;
}

.pdf-merger-spinner__circle:before {
  content: '';
  box-sizing: border-box;
  position: absolute;
  top: 0;
  left: 0;
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  border-top: 0.3rem solid var(--primary-color);
  border-right: 0.3rem solid transparent;
  animation: pdf-merger-spin 0.6s linear infinite;
}

@keyframes pdf-merger-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
